import { useEffect, useRef, useState } from 'react'
import { useConfidenceThreshold, useUpdateConfidenceThreshold } from '@/hooks/use-settings'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Slider } from '@/components/ui/slider'

export function ConfidenceThresholdSettings() {
  const { data: serverValue, isLoading } = useConfidenceThreshold()
  const updateThreshold = useUpdateConfidenceThreshold()
  const [localValue, setLocalValue] = useState(70)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Sync local state with server value when it loads
  useEffect(() => {
    if (serverValue !== undefined) {
      setLocalValue(serverValue)
    }
  }, [serverValue])

  const handleSliderChange = (values: number[]) => {
    const newValue = values[0]
    setLocalValue(newValue)

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new debounce timer
    debounceTimerRef.current = setTimeout(() => {
      updateThreshold.mutate(newValue)
    }, 500)
  }

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return (
    <Card>
      <CardHeader>
        <CardTitle>Confidence Threshold</CardTitle>
        <CardDescription>
          Minimum confidence level required for extraction results to be accepted
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            <div className="h-8 bg-muted animate-pulse rounded" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 pr-8">
                <Slider
                  value={[localValue]}
                  onValueChange={handleSliderChange}
                  min={0}
                  max={100}
                  step={1}
                  className="w-full"
                />
              </div>
              <div className="text-2xl font-bold min-w-[4rem] text-right">
                {localValue}%
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Extraction results with confidence below {localValue}% will be flagged for review
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
